import React, { FunctionComponent, useContext, useEffect } from 'react';
import { Event } from 'features/events/Event';
import { ThemeContext } from 'features/theme/theme';
import { UserAgentContext } from 'features/user-agent/userAgent';
import { Helmet } from 'react-helmet';
import {
  Route,
  Routes,
  useLocation,
  matchPath,
  useNavigate
} from 'react-router-dom';
import './App.scss';
import { AuthModal } from 'features/auth/AuthModal';
import ReactModal from 'react-modal';
import classNames from 'classnames';
import { Channel } from 'features/channel/Channel';
import { AutoplayPrompt } from 'features/events/AutoplayPrompt';
import { ChannelEmbed } from 'features/channel/components/ChannelEmbed';
import { ChannelHome } from 'features/channel/components/ChannelHome';
import { ChannelAbout } from 'features/channel/components/ChannelAbout';
import { ChannelEvents } from 'features/channel/components/ChannelEvents';
import { ChannelRecordings } from 'features/channel/components/ChannelRecordings';
import { AnimatePresence } from 'framer-motion';
import { Alert } from 'features/alert/alert';
import { useSelector } from 'react-redux';
import { RootState } from 'rootReducer';
import { Privacy } from 'features/privacy/privacy';
import { ChannelSubscription } from 'features/channel_subscription/channelSubscription';
import { EventMiniPlayer } from 'features/events/components/EventMiniPlayer';
import { RecordingMiniPlayer } from 'features/recordings/components/RecordingMiniPlayer';
import { SendRecordingListenEvents } from 'features/recordings/components/SendRecordingListenEvents';
import { SendPlayRecordingEvent } from 'features/recordings/components/SendPlayRecordingEvent';
import { Error404 } from 'components/ErrorPages/Error404';
import { PrivacyNotice } from 'features/privacy/privacyNotice';
import {
  isMobileOnly,
  isTablet,
  isDesktop,
  browserName,
  browserVersion
} from 'react-device-detect';
import { StateTester } from 'features/statetester/StateTester';
import { Recording } from 'features/recordings/Recording';
import { useNativeEvents } from 'hooks/useNativeEvents';
import { useAudio } from 'features/audio/audio';
import { useDispatch } from 'react-redux';
import { useBannedChannelChecker } from 'hooks/useBannedChannelChecker';
import { useBackButtonListener } from 'hooks/useBackButtonListener';
import { WebSocketContext } from 'features/hub/hub';
import { useOnceEffect } from 'hooks/useOnceEffect';
import { setCurrentVolume } from 'features/audio/audioSlice';

const AnimatedRouteSwitch: FunctionComponent = () => {
  const location = useLocation();

  useBackButtonListener();
  useBannedChannelChecker();

  useEffect(() => {
    return () => {
      window.history.replaceState({}, document.title);
    };
  }, []);

  return (
    <AnimatePresence initial={false}>
      <Routes location={location} key={location.pathname}>
        <Route element={<Channel />}>
          <Route path="/" element={<ChannelHome />} />
          <Route path="/about" element={<ChannelAbout />} />
          <Route path="/events" element={<ChannelEvents />} />
          <Route path="/recordings" element={<ChannelRecordings />} />
        </Route>
        <Route path="/embed" element={<ChannelEmbed />} />
        <Route path="/events/:id" element={<Event />} />
        <Route path="/recordings/:id" element={<Recording />} />
        <Route path="*" element={<Error404 />} />
      </Routes>
    </AnimatePresence>
  );
};

const App: FunctionComponent = () => {
  const {
    deviceClassName,
    isNativeAppWebview,
    isDACreatorApp,
    isListenerAppWebview
  } = useContext(UserAgentContext);
  const { theme, themeContrast, themeColor } = useContext(ThemeContext);
  const {
    postMessage,
    dynamicSizeUpdatesEvent,
    qWebChannel,
    eventWindowEvents
  } = useNativeEvents();
  const { connectToSocket, leaveCrowd } = useContext(WebSocketContext);
  const { setVolume, volume } = useAudio();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isEventPath = matchPath('/events/:id', pathname);
  const isRecordingPath = matchPath('/recordings/:id', pathname);

  const { requiresAccesscode, requiresChannelSubscription } = useSelector(
    (state: RootState) => {
      return {
        requiresAccesscode: state.channel.requiresAccesscode,
        requiresChannelSubscription: state.channel.requiresChannelSubscription
      };
    }
  );

  useEffect(() => {
    if (isNativeAppWebview) {
      postMessage('javascriptLoaded');
      dynamicSizeUpdatesEvent();
    }
  }, [postMessage, dynamicSizeUpdatesEvent, isNativeAppWebview]);

  useEffect(() => {
    connectToSocket();
  }, [connectToSocket]);

  useEffect(() => {
    return () => {
      leaveCrowd();
    };
  }, [leaveCrowd]);

  useEffect(() => {
    if (isDACreatorApp) {
      qWebChannel();
    }
  }, [isDACreatorApp, qWebChannel]);

  useEffect(() => {
    if (isNativeAppWebview) {
      eventWindowEvents(pathname, navigate);
    }
  }, [isNativeAppWebview, eventWindowEvents, pathname, navigate]);

  useEffect(() => {
    if (isListenerAppWebview) {
      postMessage({
        requiresAccesscode: requiresAccesscode
      });
    }
  }, [requiresAccesscode, isListenerAppWebview, postMessage]);

  useOnceEffect(() => {
    dispatch(setCurrentVolume(volume));
  });

  useEffect(() => {
    const handleUnload = () => {
      if (volume === 0) {
        setVolume(1);
      }
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, [volume, setVolume]);

  const RenderHelmetTags = () => {
    return (
      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link
          rel="preconnect"
          href="https://fonts.gstatic.com"
          crossOrigin="true"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,400..700;1,400..700&display=swap"
          rel="stylesheet"
        />
        <meta name="msapplication-TileColor" content={themeColor} />
        <meta name="theme-color" content={themeColor} />
        <html className={classNames(deviceClassName)} />
        <body
          className={classNames(
            theme,
            themeContrast,
            `${browserName.toLowerCase()}-${browserVersion}`,
            {
              mobile: isMobileOnly,
              tablet: isTablet,
              desktop: isDesktop,
              'body-full-screen': isEventPath || isRecordingPath
            }
          )}
        />
      </Helmet>
    );
  };

  if (requiresAccesscode) {
    return (
      <>
        <RenderHelmetTags />
        <Privacy />
      </>
    );
  }

  if (requiresChannelSubscription) {
    return (
      <>
        <RenderHelmetTags />
        <ChannelSubscription />
        <AuthModal />
      </>
    );
  }

  return (
    <>
      <RenderHelmetTags />
      <Alert />
      <AnimatedRouteSwitch />
      <AuthModal />
      <AutoplayPrompt />
      <EventMiniPlayer />
      <RecordingMiniPlayer />
      <PrivacyNotice />
      <SendRecordingListenEvents />
      <SendPlayRecordingEvent />
      {(process.env.REACT_APP_ENV_NAME === 'development' ||
        process.env.REACT_APP_ENV_NAME === 'staging') && <StateTester />}
    </>
  );
};

ReactModal.setAppElement('#root');

export default App;
